/**
 * Wizard Subdomain Selection Modal
 * Used in Smart Wizard to select subdomains for a domain BEFORE domain pack creation
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Plus,
  Info,
} from 'lucide-react';

import { suggestSubdomains } from '../../lib/api/domainPacksApi';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Label } from '../ui/Label';

interface WizardSubdomainSelectModalProps {
  domainName: string;
  industry: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedSubdomains: string[], manualSubdomains: Array<{ name: string; description: string; scope: string }>) => void;
  existingSubdomains?: Array<{
    subdomain_id?: string;
    name: string;
    description: string;
    scope?: string;
    relevance_score?: number;
  }>;
}

interface Suggestion {
  name: string;
  description: string;
  scope: string;
  rationale: string;
}

export const WizardSubdomainSelectModal: React.FC<WizardSubdomainSelectModalProps> = ({
  domainName,
  industry,
  isOpen,
  onClose,
  onConfirm,
  existingSubdomains,
}) => {
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualScope, setManualScope] = useState('');
  const [manualSubdomains, setManualSubdomains] = useState<Array<{ name: string; description: string; scope: string }>>([]);

  // Fetch AI suggestions if no existing subdomains
  const { data, isLoading, error } = useQuery({
    queryKey: ['wizard-subdomain-suggestions', domainName, industry],
    queryFn: () => suggestSubdomains(domainName, industry, 5),
    enabled: isOpen && !existingSubdomains,
  });

  const handleToggleSelection = (index: number) => {
    const newSelected = new Set(selectedSuggestions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSuggestions(newSelected);
  };

  const handleAddManual = () => {
    if (!manualName.trim()) return;

    setManualSubdomains([
      ...manualSubdomains,
      {
        name: manualName.trim(),
        description: manualDescription.trim(),
        scope: manualScope.trim(),
      }
    ]);

    // Reset form
    setManualName('');
    setManualDescription('');
    setManualScope('');
    setShowManualEntry(false);
  };

  const handleRemoveManual = (index: number) => {
    setManualSubdomains(manualSubdomains.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    // Get selected AI suggestions
    const suggestions = existingSubdomains || data?.suggestions || [];
    const selected = Array.from(selectedSuggestions).map(i => suggestions[i].name);

    onConfirm(selected, manualSubdomains);
    handleClose();
  };

  const handleClose = () => {
    setSelectedSuggestions(new Set());
    setManualSubdomains([]);
    setShowManualEntry(false);
    setManualName('');
    setManualDescription('');
    setManualScope('');
    onClose();
  };

  if (!isOpen) return null;

  const suggestions = existingSubdomains || data?.suggestions || [];
  const totalSelected = selectedSuggestions.size + manualSubdomains.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-primary-600" />
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">
                Select Subdomains
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                For: <span className="font-semibold">{domainName}</span>
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading && !existingSubdomains && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              <span className="ml-3 text-neutral-700">Getting AI suggestions...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Failed to generate suggestions</p>
                <p className="text-sm text-red-700 mt-1">
                  {(error as any)?.response?.data?.detail || 'An error occurred'}
                </p>
              </div>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="space-y-4">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-primary-900">
                  <p className="font-semibold">
                    {existingSubdomains ? 'Available Subdomains' : 'AI-Generated Suggestions'}
                  </p>
                  <p className="mt-1">
                    {existingSubdomains
                      ? 'Select the subdomains you want to include in your research.'
                      : 'These are AI-suggested subdomains for comprehensive coverage. Select the ones you want to research.'}
                  </p>
                </div>
              </div>

              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedSuggestions.has(index)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300'
                  }`}
                  onClick={() => handleToggleSelection(index)}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {selectedSuggestions.has(index) ? (
                        <CheckCircle2 className="w-5 h-5 text-primary-600" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-neutral-300 rounded" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-neutral-900">{suggestion.name}</h3>
                      <p className="text-sm text-neutral-700 mt-1">{suggestion.description}</p>

                      {suggestion.scope && (
                        <div className="mt-2 text-sm">
                          <span className="font-semibold text-neutral-900">Scope: </span>
                          <span className="text-neutral-700">{suggestion.scope}</span>
                        </div>
                      )}

                      {'rationale' in suggestion && (
                        <div className="mt-2 bg-white rounded p-2 border border-neutral-200">
                          <span className="font-semibold text-neutral-900 text-sm">Why this matters: </span>
                          <span className="text-neutral-700 text-sm">{suggestion.rationale}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Manual Subdomains */}
          {manualSubdomains.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="font-semibold text-neutral-900">Custom Subdomains</h3>
              {manualSubdomains.map((sub, index) => (
                <div key={index} className="border border-green-200 bg-green-50 rounded-lg p-4 flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-neutral-900">{sub.name}</h4>
                    <p className="text-sm text-neutral-700 mt-1">{sub.description}</p>
                    {sub.scope && (
                      <p className="text-sm text-neutral-600 mt-1">
                        <span className="font-semibold">Scope:</span> {sub.scope}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveManual(index)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add Manual Subdomain */}
          <div className="mt-6">
            {!showManualEntry ? (
              <Button
                variant="outline"
                onClick={() => setShowManualEntry(true)}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Custom Subdomain
              </Button>
            ) : (
              <div className="border border-neutral-300 rounded-lg p-4 space-y-4">
                <div>
                  <Label htmlFor="manual_name">Subdomain Name *</Label>
                  <Input
                    id="manual_name"
                    value={manualName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualName(e.target.value)}
                    placeholder="e.g., Inventory Management"
                  />
                </div>
                <div>
                  <Label htmlFor="manual_description">Description</Label>
                  <textarea
                    id="manual_description"
                    value={manualDescription}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setManualDescription(e.target.value)}
                    placeholder="What this subdomain covers"
                    className="w-full min-h-[80px] px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <Label htmlFor="manual_scope">Scope (Optional)</Label>
                  <Input
                    id="manual_scope"
                    value={manualScope}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualScope(e.target.value)}
                    placeholder="What's included and excluded"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddManual} disabled={!manualName.trim()}>
                    Add Subdomain
                  </Button>
                  <Button variant="outline" onClick={() => setShowManualEntry(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-neutral-200">
          <div className="text-sm text-neutral-600">
            {totalSelected > 0 && (
              <span className="font-semibold text-primary-600">
                {totalSelected} subdomain{totalSelected > 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={totalSelected === 0}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Confirm Selection ({totalSelected})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
